import { useMemo } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../../core';
import { KeyConstants } from '../../../types/enums';
import FAIcon from '../../../Icons/Icons';
import type { PaginationProps } from '../../types/propType';
import { scrollToTop } from '../Pagination';
import { usePaginationPosition } from '../../../hooks';
import { getPaginationRange } from '../utils';
import { RowsPerPageSelectorVariant } from '../../types/enums';
import RowsPerPageSelector from '../RowsPerPageSelector/RowsPerPageSelector';
import PaginationRecordRange from '../PaginationRecordRange/PaginationRecordRange';
import styles from './RowPerPageAndAllPages.module.scss';

const MAX_SLOTS = 5;
const DOTS = '...';
const cx = classNames.bind(styles);

const RowPerPageAndAllPages = ({
  totalRecords,
  currentPage = 0,
  rowsPerPage = 5,
  updatePaginationData,
  startRecordIndex = () => 1,
  endRecordIndex,
  goToPreviousPage,
  goToNextPage,
  pageSizeData,
  scrollToTopRefId,
  enableStickyPagination,
  formatPaginationCallback,
}: PaginationProps) => {
  usePaginationPosition(enableStickyPagination, currentPage, totalRecords, scrollToTopRefId, rowsPerPage);

  const numberOfPages = Math.ceil(totalRecords / rowsPerPage);

  const pageArr = useMemo(
    () => getPaginationRange(currentPage, totalRecords, rowsPerPage, MAX_SLOTS),
    [currentPage, totalRecords, rowsPerPage],
  );

  const goToPage = (page: number) => {
    updatePaginationData({
      currentPage: page,
      recordStartIndex: startRecordIndex(page, rowsPerPage),
      recordEndIndex: endRecordIndex?.(page, rowsPerPage),
    });
    scrollToTop(scrollToTopRefId);
  };

  const shouldHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, callback: () => void) => {
    if ([KeyConstants.ENTER, KeyConstants.SPACE].includes(e.code as KeyConstants)) {
      callback();
    }
  };

  const isDisabledPrev = currentPage === 0;
  const isDisabledNext = numberOfPages === currentPage + 1;

  const hasRowSelector = !!pageSizeData;
  const hasRecordRange = !!formatPaginationCallback;

  const ContainerClassName = cx('pagination-container', {
    'sticky-pagination': enableStickyPagination,
    'space-between': hasRowSelector || (hasRecordRange && hasRowSelector),
  });

  return (
    <div id={'pagination-container'} className={ContainerClassName}>
      {pageSizeData && (
        <RowsPerPageSelector
          rowsPerPage={rowsPerPage}
          pageSizeData={pageSizeData}
          currentPage={currentPage}
          totalRecords={totalRecords}
          startRecordIndex={startRecordIndex}
          variant={RowsPerPageSelectorVariant.COMPACT}
          {...optionalProp('endRecordIndex', endRecordIndex)}
          {...optionalProp('updatePaginationData', updatePaginationData)}
          {...optionalProp('scrollToTopRefId', scrollToTopRefId)}
        />
      )}
      <div className={cx('pagination-controls', { 'horizontally-center': hasRecordRange && !hasRowSelector })}>
        <div
          role="button"
          tabIndex={0}
          className={cx('icon-container', { disabled: isDisabledPrev })}
          data-testid="pagination-back-button"
          onClick={goToPreviousPage}
          onKeyDown={(e) => shouldHandleKeyDown(e, goToPreviousPage!)}
          aria-label="Previous page"
        >
          <FAIcon iconName={'angle-left'} className={styles['icon']} />
        </div>
        <div className={styles['page-numbers']}>
          {pageArr.map((page, index) => {
            if (page === DOTS) {
              return (
                <FAIcon key={`ellipsis-${page}-${index + 1}`} iconName={'ellipsis'} className={styles['ellipsis']} />
              );
            }
            const pageNumber = typeof page === 'number' ? page - 1 : 0;
            const isActive = pageNumber === currentPage;
            return (
              <div
                role="button"
                tabIndex={0}
                key={`page-${page}-${index + 1}`}
                data-testid={`page-numbers-${pageNumber}`}
                className={cx('page-number', { active: isActive })}
                onClick={() => goToPage(pageNumber)}
                onKeyDown={(e) => shouldHandleKeyDown(e, () => goToPage(pageNumber))}
                aria-label={`Go to page ${page}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {page}
              </div>
            );
          })}
        </div>
        <div
          role="button"
          tabIndex={0}
          className={cx('icon-container', { disabled: isDisabledNext })}
          data-testid="pagination-next-button"
          onClick={goToNextPage}
          onKeyDown={(e) => shouldHandleKeyDown(e, goToNextPage!)}
          aria-label="Next page"
        >
          <FAIcon iconName={'angle-right'} className={styles['icon']} />
        </div>
      </div>
      {formatPaginationCallback && (
        <PaginationRecordRange
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalRecords={totalRecords}
          startRecordIndex={startRecordIndex}
          endRecordIndex={endRecordIndex || (() => 0)}
          formatPaginationCallback={formatPaginationCallback}
        />
      )}
    </div>
  );
};

export default RowPerPageAndAllPages;
